import { marketsConfig } from '$lib/config/markets';
import { allTokensConfig } from '$lib/tokensConfig';
import type { Market, MarketIdentifiers } from '$lib/types/market';
import type { PythPrice } from '$lib/types/pyth';
import type { Token, TokenSymbol, TokenUI } from '$lib/types/tokens';
import { optimism, type Chain } from '@wagmi/core/chains';
import BigNumber from 'bignumber.js';
import { ethers } from 'ethers';
import type { Order } from '$lib/types/order';
import type { CompetitionConfig } from '$lib/types/leaderboard';
import type { UserMargin } from '$lib/types/user';
import { getDisplayName } from '$lib/perps';

export function basePowerAdjusted(num: string, decimals: string, decimalsTo = '18'): BigNumber {
	return new BigNumber(num).multipliedBy(`1e${parseInt(decimalsTo) - parseInt(decimals)}`);
}

export function getNumBackInUnits(amt: string, unit: number, precision = -1, adapt = false) {
	if (!amt) return '0';
	if (adapt) {
		precision = new BigNumber(amt).div(10 ** unit).isGreaterThan(1000) ? 0 : precision;
	}
	if (precision !== -1) {
		BigNumber.config({ ROUNDING_MODE: BigNumber.ROUND_HALF_EVEN });
		return new BigNumber(amt.toString()).div(10 ** unit).toFixed(precision);
	}
	return new BigNumber(amt.toString()).div(10 ** unit).toFixed(0);
}

export function getDecimalOfToken(token: TokenSymbol, network: Chain = optimism): number {
	return parseInt(allTokensConfig[network.id][token].decimals);
}

export function getPrecisionOfToken(token: TokenSymbol, network: Chain = optimism): number {
	return allTokensConfig[network.id][token].showDecimals;
}

export function getPrecisionOfMarket(marketAsset: string | undefined): number {
	return marketsConfig[marketAsset as string].showDecimals;
}

export function nFormatter(num: any, digits: number) {
	const lookup = [
		{ value: 1, symbol: '' },
		{ value: 1e3, symbol: 'K' },
		{ value: 1e6, symbol: 'M' },
		{ value: 1e9, symbol: 'B' },
		{ value: 1e12, symbol: 'T' }
	];
	const rx = /(\.[0-9]*[0-9])$/;
	const item = lookup
		.slice()
		.reverse()
		.find((item) => {
			return num >= item.value;
		});
	return item ? (num / item.value).toFixed(digits).replace(rx, '$1') + item.symbol : '0';
}

export function debounce(node: any, params: any) {
	let timer: any;

	return {
		update() {
			clearTimeout(timer);
			timer = setTimeout(params.func, params.duration);
		},
		destroy() {
			clearTimeout(timer);
		}
	};
}

export function toBN(val: string, decimal = 18) {
	if (val.includes('e')) {
		if (parseFloat(val) > 1) {
			const x = val.split('.');
			const y = x[1].split('e+');
			const exponent = parseFloat(y[1]);
			const newVal = x[0] + y[0] + '0'.repeat(exponent - y[0].length);
			val = newVal;
		} else {
			val = parseFloat(val).toFixed(18);
		}
	} else if (val.includes('.') && val.split('.')[1].length > 18) {
		// console.warn(`Warning: toBN of val with more than 18 decimals. Stripping excess. (${val})`);
		const x = val.split('.');
		x[1] = x[1].slice(0, 18);
		val = x[0] + '.' + x[1];
	}
	return ethers.utils.parseUnits(val, decimal);
}

export function getShortAddress(address: string) {
	if (address) return address.substr(0, 4) + '..' + address.substr(address.length - 3);
	else return '';
}

export function getDate(timestamp: string) {
	const months_arr = [
		'Jan',
		'Feb',
		'Mar',
		'Apr',
		'May',
		'Jun',
		'Jul',
		'Aug',
		'Sep',
		'Oct',
		'Nov',
		'Dec'
	];
	const date = new Date(parseInt(timestamp) * 1000);
	const day = date.getDate();
	const month = months_arr[date.getMonth()];
	const year = date.getFullYear();
	return day.toString() + '  ' + month + ', ' + year.toString();
}

function formatMinutes(minutes: string) {
	if (minutes.length > 1) {
		return minutes;
	} else {
		return '0' + minutes;
	}
}
export function getTime(timestamp: string) {
	const date = new Date(parseInt(timestamp) * 1000);
	let hours = date.getHours();
	const minutes = date.getMinutes();
	let meridan = 'AM';
	if (hours >= 12) {
		meridan = 'PM';
		hours -= 12;
	}
	return hours.toString() + ':' + formatMinutes(minutes.toString()) + ' ' + meridan;
}

export function getPastTimestampInMillisecondsDeltaDaysOrMonths(days: number, months: number) {
	const today = new Date();
	today.setDate(today.getDate() - days);
	today.setMonth(today.getMonth() - months);
	return today.getTime();
}

export function num(x: any) {
	const parts = x.toString().split('.');
	let isNeg = false;
	if (parts[0].indexOf('-') !== -1) {
		const temp = parts[0].split('-')[0];
		if (temp.replace(/[^0-9\.]/g, '') === '') {
			isNeg = true;
		}
	}

	for (let i = 0; i < parts.length; i++) {
		parts[i] = parts[i].replace(/[^0-9\.]/g, '');
	}
	const chars = parts[0].split('').reverse();
	let str = '';
	for (let i = 0; i < chars.length; i++) {
		if (i % 3 === 0 && i > 0) {
			str += ',';
		}
		str += chars[i];
	}
	parts[0] = str.split('').reverse().join('');
	if (isNeg) parts[0] = '-' + parts[0];
	return parts.join('.');
}

export function addDollarSign(
	value: number | string,
	precision = 2,
	convertNumberToInternationalSystem?: boolean
): string {
	const numWithPrecision = convertNumberToInternationalSystem
		? convertToInternationalCurrencySystem(value)
		: precision === -1
		? num(Math.abs(Number(value)))
		: num(Math.abs(Number(value)).toFixed(precision));

	return new BigNumber(value).isNegative() ? '-$' + numWithPrecision : '$' + numWithPrecision;
}

export function sanitizeInputs(val: string) {
	const parts = val.toString().split('.');
	let str = '';
	for (let i = 0; i < parts.length; i++) {
		parts[i] = parts[i].replace(/[^0-9\.]/g, '');
		if (i > 0) {
			str += parts[i];
		}
	}
	if (parts.length > 1) str = parts[0] + '.' + str;
	else str = parts[0];
	return str;
}

export function makeBodyUnscrollable() {
	document.getElementsByTagName('body')[0].style.overflow = 'hidden';
}

export function makeBodyScrollable() {
	document.getElementsByTagName('body')[0].style.overflow = 'auto';
}

export function trimString(s: string, len: number) {
	if (s.length > len) {
		return s.substring(0, len) + '...';
	} else {
		return s;
	}
}

export function getPriceFromPythPrice(pyth: PythPrice) {
	const exp = 18 + pyth.expo;
	return new BigNumber(pyth.price).multipliedBy(`1e${exp}`).toString();
}

export function pad(num: string | number, size: number) {
	num = num.toString();
	while (num.length < size) num = '0' + num;
	return num;
}

export function nextWednesdayTimestampUTC() {
	const now = new Date();
	const day = now.getUTCDay();
	const diff = day <= 3 ? 3 - day : 10 - day;
	now.setUTCDate(now.getUTCDate() + diff);
	now.setUTCHours(0, 0, 0, 0);
	return now.getTime() / 1000;
}

export function getPercentageFromBasisPoints(pts: number, decimals = 2) {
	const percentage = pts / 100;
	return percentage.toFixed(decimals);
}

export function isSupportedNetwork(chainId: number | undefined): boolean {
	return chainId === optimism.id;
}

export function getMarketPrice(m: Market) {
	if (m.currentOraclePrice) {
		const skew = new BigNumber(m.longOI).minus(m.shortOI);
		const indexMultiplier = new BigNumber(1).plus(skew.div(m.skewScale)).toString();
		return new BigNumber(m.currentOraclePrice).times(indexMultiplier).toFixed(0);
	} else {
		return '0';
	}
}

export function getOrderIdUI(order: Order) {
	if (order.orderId) {
		if (typeof order.orderId == 'number') return order.orderId.toString();
		return order.orderId.slice(0, order.orderId.lastIndexOf('-'));
	}
	if (order.requestId) {
		return order.requestId;
	}
	return '';
}

export function addCompetitionStatus(data: CompetitionConfig) {
	if (data.startTimestamp > Date.now() / 1000) {
		data.status = 'Upcoming';
	} else if (data.endTimestamp < Date.now() / 1000) {
		data.status = 'Finished';
	} else {
		data.status = 'Ongoing';
	}
	return data;
}

export function sortInDescending(tokensAllowedForDepositBalance: Record<string, Token | TokenUI>) {
	return Object.entries(tokensAllowedForDepositBalance)
		.sort((a, b) => {
			const aToken: Token = a[1];
			const bToken: Token = b[1];

			if (
				basePowerAdjusted(aToken.balanceUSD, aToken.decimals).gt(
					basePowerAdjusted(bToken.balanceUSD, bToken.decimals)
				)
			) {
				return -1;
			}

			if (
				basePowerAdjusted(bToken.balanceUSD, bToken.decimals).gt(
					basePowerAdjusted(aToken.balanceUSD, aToken.decimals)
				)
			) {
				return +1;
			}

			return 0;
		})
		.map((entry) => entry[1]);
}

export function sortInDescendingWithSUSDOnTop(
	tokensAllowedForDepositBalance: Record<string, Token | TokenUI>
) {
	const properlySorted = sortInDescending(tokensAllowedForDepositBalance);
	const susdIndex = properlySorted.findIndex((token) => token.symbol === 'sUSD');
	const susdValue = properlySorted[susdIndex];
	properlySorted.splice(susdIndex, 1);
	properlySorted.unshift(susdValue);
	return properlySorted;
}

export function sortMarginsInDescending(margins: UserMargin[]) {
	return margins.sort((a, b) => {
		const aBigNumber = new BigNumber(a.accessibleMargin);
		const bBigNumber = new BigNumber(b.accessibleMargin);
		if (aBigNumber.gt(bBigNumber)) return -1;

		if (bBigNumber.gt(aBigNumber)) return 1;

		return 0;
	});
}

export function convertToInternationalCurrencySystem(
	amount: number | string,
	maximumFractionDigits = 3
) {
	try {
		const language = 'en';
		// eslint-disable-next-line
		return Intl.NumberFormat(language, {
			notation: 'compact',
			maximumFractionDigits
		}).format(parseFloat(amount.toString()));
	} catch (err) {
		console.log(err);
		return amount.toString();
	}
}

export function filterMarketsBasedOnSearch(markets: Market[], searchText: string) {
	return markets?.filter((market) => {
		return (
			getDisplayName(market.marketIdentifier)
				.split('-')[0]
				.toLowerCase()
				.includes(searchText?.toLowerCase()) || searchText == ''
		);
	});
}

/**
 * The function calculates the time remaining between the current date and a specified end time.
 * @param {string} endtime - The `endtime` parameter is a string representing the end time of an event
 * or countdown. It should be in a format that can be parsed by the `Date.parse()` method
 * @returns an object with the following properties:
 * - total: the total time remaining in milliseconds
 * - days: the number of days remaining
 * - hours: the number of hours remaining
 * - minutes: the number of minutes remaining
 * - seconds: the number of seconds remaining
 */
export const calculateTimeRemaining = (endtime: number) => {
	const total = Date.parse(new Date(endtime).toString()) - Date.parse(new Date().toString());
	const seconds = Math.floor((total / 1000) % 60);
	const minutes = Math.floor((total / 1000 / 60) % 60);
	const hours = Math.floor((total / (1000 * 60 * 60)) % 24);
	const days = Math.floor(total / (1000 * 60 * 60 * 24));

	return {
		total,
		days,
		hours,
		minutes,
		seconds
	};
};

export const capitalizeFirstLetter = (str: string) => {
	return str.charAt(0).toUpperCase() + str.slice(1);
};
